import React, {useCallback, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {RootState, useAppDispatch} from './store';
import {useLocation} from 'react-router-dom';
import {AuthClientSingleton} from './utils/auth';
import {getLocalAccessToken} from './actions/auth';
import {MessageType, parseMessage, sendMessage} from './messaging';

function allowDevLogin() {
    return window.location.hostname === 'localhost' || window.location.hostname.startsWith('staging.');
}

const noAuthFilter: ((path: string) => boolean)[] = [
    (path) => path === '/logout',
    (path) => allowDevLogin() && path === '/_login',
    (path) => allowDevLogin() && path === '/_callback',
    (path) => path.startsWith('/trial'),
];

const awaitAuthFilter: ((path: string) => boolean)[] = [(path) => path === '/transformation/embed'];

export function AuthProvider(props: {children: React.ReactNode}) {
    const authProps = useSelector((state: RootState) => state.auth);
    const location = useLocation();
    const dispatch = useAppDispatch();
    let [noAuth, setNoAuth] = React.useState(false);
    let setupMsgHandler = useRef(false);

    const messageHandler = useCallback(
        (e: MessageEvent) => {
            console.debug('authProvider: received message', e.data);
            const type: MessageType = e.data.type;
            switch (type) {
                case 'PerceptPixel.auth':
                    const data = parseMessage(e, type);
                    AuthClientSingleton.load(data);
                    dispatch(getLocalAccessToken());
                    sendMessage('PerceptPixel.success', 'authenticated', e.source as Window);
                    break;
                case 'PerceptPixel.ping':
                    sendMessage('PerceptPixel.pong', 'pong', e.source as Window);
                    break;
            }
        },
        [dispatch],
    );

    useEffect(() => {
        if (authProps.isAuthenticated) {
            console.debug('authProvider: authenticated');
            return;
        }

        let loading = false;
        localStorage.removeItem('user_last_path');
        const {pathname, search} = location;
        const fullPath = `${pathname}${search}`;
        const noAuth = noAuthFilter.some((fn) => fn(pathname));
        const awaitAuth = awaitAuthFilter.some((fn) => fn(pathname));

        // if we have a token, try loading anyway
        if (!!AuthClientSingleton.getToken()) {
            dispatch(getLocalAccessToken());
            loading = true;
        }

        console.debug('authProvider', {
            loading,
            noAuth,
            awaitAuth,
        });

        setNoAuth(noAuth);

        if (awaitAuth) {
            console.debug('authProvider: setting up message handler');
            if (!setupMsgHandler.current) {
                window.addEventListener('message', messageHandler);
                setupMsgHandler.current = true;
            }
        }

        if (!(loading || noAuth || awaitAuth)) {
            console.debug('authProvider: redirecting');
            localStorage.setItem('user_last_path', fullPath);
            window.location.href = window.location.origin + '/login';
        }
    }, [authProps.isAuthenticated, dispatch, location, messageHandler]);

    if (authProps.isAuthenticated || noAuth) {
        return <>{props.children}</>;
    } else {
        return (
            <>
                <div className='flex h-screen flex-col items-center justify-center'>
                    {authProps.error ? (
                        <>
                            <div className='text-red-500'>{authProps.error}</div>
                            <a className={'underline'} href={window.location.origin + '/dashboard/logout'}>
                                Logout
                            </a>
                        </>
                    ) : (
                        <>
                            <div className='grow'></div>
                            <div className='text-2xl text-gray-600'>Loading...</div>
                            <span className='text-sm text-gray-400'>
                                If this takes too long, try refreshing the page
                            </span>
                            <div className='grow'></div>
                            <a className={'pb-2 underline'} href={window.location.origin + '/dashboard/logout'}>
                                Logout
                            </a>
                        </>
                    )}
                </div>
            </>
        );
    }
}
